import { z } from 'zod';
import { UUIDSchema } from '../domain/models';

/**
 * Note that `MS-` vitals are originally from the MightySAT but should be used to display the
 * relevant values for any device that supports them. The measurement names are retained since it's
 * not feasible to migrate/move this data currently
 */
export enum MeasurementName {
  Activity = 'ACT',
  HeartRate = 'HTR',
  RespiratoryRate = 'RSR',
  SkinTemperature = 'STP',
  CoreTemperature = 'CTP',
  OxygenSaturation = 'MS-SpO2',
  PerfusionIndex = 'MS-Pi',
  Steps = 'STS',
  Posture = 'POS',
  Battery = 'BAT',
  RRI = 'RRI',
  ECG = 'ECG',
  Glucose = 'GLUCOSE',
  GlucoseFasting = 'GLUCOSE-FASTING',
  GlucoseBeforeMeal = 'GLUCOSE-BEFORE-MEAL',
  GlucoseAfterMeal = 'GLUCOSE-AFTER-MEAL',
  SystolicArterialBloodPressure = 'BP-SYSTOLIC',
  DiastolicArterialBloodPressure = 'BP-DIASTOLIC',
  MeanArterialBloodPressure = 'BP-MAP',
  /**
   * Blood pressure represented in the `SYS/DIA` format
   */
  CombinedArterialBloodPressure = 'BP-COMBINED',
  Weight = 'WEIGHT',
  PostureCalibration = 'POS-CALIBRATION',
  /**
   * Represent Quro early warning score
   */
  EarlyWarningScore = 'EWS',
  ButtonPress = 'BTN',
  SensorLowQuality = 'SENSOR-LOW-QUALITY',
  Fall = 'FALL',
  WearStatus = 'WEAR',
  DeviceStatus = 'DEVICE',
}

export const MeasurementNameSchema = z.nativeEnum(MeasurementName);

export enum TimeToLiveOffsetSeconds {
  Minute = 60,
  Hour = 3600,
  Day = 86400,
  Week = 604800,
  Month = 2629743,
  Year = 31556926,
}

/**
 * List of metrics that may be queried over long time periods
 */
export const longTermMetrics = [
  MeasurementName.CombinedArterialBloodPressure,
  MeasurementName.SystolicArterialBloodPressure,
  MeasurementName.DiastolicArterialBloodPressure,
  MeasurementName.MeanArterialBloodPressure,
  MeasurementName.Glucose,
  MeasurementName.GlucoseBeforeMeal,
  MeasurementName.GlucoseAfterMeal,
  MeasurementName.GlucoseFasting,
  MeasurementName.Weight,
] as const;

/**
 * Metric that may be queried over long time periods
 */
export type LongTermMetric = (typeof longTermMetrics)[number];

type MeasurementTTL = Partial<Record<MeasurementName, number>> &
  Record<LongTermMetric, number>;

const longTermMetricTTL = 6 * TimeToLiveOffsetSeconds.Month;

/**
 * TTL Seconds for metrics where it is explicitly defined
 */
export const MeasurementTTLMap: MeasurementTTL = {
  [MeasurementName.CombinedArterialBloodPressure]: longTermMetricTTL,
  [MeasurementName.SystolicArterialBloodPressure]: longTermMetricTTL,
  [MeasurementName.DiastolicArterialBloodPressure]: longTermMetricTTL,
  [MeasurementName.MeanArterialBloodPressure]: longTermMetricTTL,
  [MeasurementName.Glucose]: longTermMetricTTL,
  [MeasurementName.GlucoseBeforeMeal]: longTermMetricTTL,
  [MeasurementName.GlucoseAfterMeal]: longTermMetricTTL,
  [MeasurementName.GlucoseFasting]: longTermMetricTTL,
  [MeasurementName.Weight]: longTermMetricTTL,
};

export enum PostureReading {
  LayingDown = 1,
  Sitting = 2,
  LeaningBack = 3,
  Standing = 4,
  Walking = 5,
  Running = 6,
  Unknown = -1,
  NotFound = -2,
}

export enum PostureCalibration {
  /**
   * Sitting upright
   */
  Upright = 1,
  /**
   * The supine position means lying horizontally with the face and torso facing up
   */
  LayingSupine = 2,
}

export enum DeviceStatusReading {
  ok = 1,
  notWorn = 2,
  deviceDisconnected = 3,
  deviceCharging = 4,
  batteryLow = 5,
  batteryCritical = 6,
}

const timestampRefinement = (obj: VitalTimeRange, ctx: z.RefinementCtx) => {
  const { start, end } = obj;
  if (start > end) {
    ctx.addIssue({
      code: 'custom',
      message: 'Start timestamp cannot be greater than end timestamp',
      path: ['start', 'end'],
    });
  }

  const startDate = new Date(start);
  if (isNaN(startDate.getTime())) {
    ctx.addIssue({
      code: 'custom',
      message: 'Invalid start timestamp',
      path: ['start'],
    });
  }

  const endDate = new Date(end);
  if (isNaN(endDate.getTime())) {
    ctx.addIssue({
      code: 'custom',
      message: 'Invalid end timestamp',
      path: ['end'],
    });
  }
};

const optionalTimestampRefinement = (
  obj: VitalTimeRangeOptional,
  ctx: z.RefinementCtx,
) => {
  const { start, end } = obj;
  if (start && end && start > end) {
    ctx.addIssue({
      code: 'custom',
      message: 'Start timestamp cannot be greater than end timestamp',
      path: ['start', 'end'],
    });
  }

  if (start) {
    const startDate = new Date(start);
    if (isNaN(startDate.getTime())) {
      ctx.addIssue({
        code: 'custom',
        message: 'Invalid start timestamp',
        path: ['start'],
      });
    }
  }

  if (end) {
    const endDate = new Date(end);
    if (isNaN(endDate.getTime())) {
      ctx.addIssue({
        code: 'custom',
        message: 'Invalid end timestamp',
        path: ['end'],
      });
    }
  }
};

const VitalTimeRangeOptionalSchema = z.object({
  patientId: UUIDSchema.describe('The ID of the patient'),
  start: z
    .number()
    .int()
    .min(0)
    .optional()
    .describe('The start timestamp in epoch format'),
  end: z
    .number()
    .int()
    .min(0)
    .optional()
    .describe('The end timestamp in epoch format'),
});
type VitalTimeRangeOptional = z.infer<typeof VitalTimeRangeOptionalSchema>;

const VitalTimeRangeSchema = z.object({
  start: z
    .number()
    .int()
    .min(0)
    .describe('The start timestamp in epoch format'),
  end: z.number().int().min(0).describe('The end timestamp in epoch format'),
});
type VitalTimeRange = z.infer<typeof VitalTimeRangeSchema>;

export const MeasurementSchema = z.object({
  ts: z.number().describe('The timestamp when the measurement was taken'),
  value: z.string().describe('The value of the measurement'),
  type: MeasurementNameSchema.describe('The type of the measurement'),
});
export type Measurement = z.infer<typeof MeasurementSchema>;

export const VitalMeasurementSchema = MeasurementSchema.extend({
  sensorId: z
    .string()
    .describe('The ID of the sensor that created the measurement'),
  relayTs: z
    .string()
    .describe(
      'The timestamp when the measurement was received on the relay device',
    ),
  cloudTs: z
    .string()
    .describe('The timestamp when the measurement was received by the cloud'),
  metadata: z
    .record(z.string())
    .describe('Additional metadata for the measurement'),
});
export type VitalMeasurement = z.infer<typeof VitalMeasurementSchema>;

export const BaseTimeSeriesRecordSchema = z.object({
  ts: z.number().describe('The timestamp when the recording happened'),
  value: z.number().describe('The measurement value of the record'),
  measurementName: z
    .nativeEnum(MeasurementName)
    .describe('The name of the measurement that is being recorded'),
});

export const SensorTimeSeriesRecordSchema = BaseTimeSeriesRecordSchema.extend({
  sensorId: z
    .string()
    .describe('The ID of the sensor that created the measurement'),
});

export type BaseTimeSeriesRecord = z.infer<typeof SensorTimeSeriesRecordSchema>;

export const VitalsResultSchema = z
  .array(SensorTimeSeriesRecordSchema)
  .optional();
export const LatestResultSchema = SensorTimeSeriesRecordSchema.optional();

export type VitalsResult = z.infer<typeof VitalsResultSchema>;
export type VitalsLatestResult = z.infer<typeof LatestResultSchema>;

const measurementNameSchema = z.nativeEnum(MeasurementName);

export const VitalsResultMeasuresSchema = z.record(
  measurementNameSchema,
  VitalsResultSchema,
);
export const LatestResultMeasuresSchema = z.record(
  measurementNameSchema,
  LatestResultSchema,
);
export const LatestResultNotesMeasuresSchema = z.record(
  measurementNameSchema,
  BaseTimeSeriesRecordSchema.extend({
    sensorId: z.string().optional(),
  }),
);
export type LatestResultNotesMeasures = z.infer<
  typeof LatestResultNotesMeasuresSchema
>;

export type VitalsResultMeasures = z.infer<typeof VitalsResultMeasuresSchema>;
export type VitalsLatestResultMeasures = z.infer<
  typeof LatestResultMeasuresSchema
>;

export const LiveDataMeasuresSchema = z.object({
  latest: LatestResultMeasuresSchema,
  ecg: SensorTimeSeriesRecordSchema.array(),
});
/**
 * Single Entry for the data provided on the Vitals Websocket data
 */
export type VitalsLiveDataMeasures = z.infer<typeof LiveDataMeasuresSchema>;

export const VitalsMeasurementRequestSchema = z.object({
  patientId: UUIDSchema,
  measurementName: measurementNameSchema,
  start: z.number(),
  end: z.number(),
});
export type VitalsMeasurementRequest = z.infer<
  typeof VitalsMeasurementRequestSchema
>;

type PatientId = string;

/**
 * Payload of `LiveDataMeasures` for a respective `PatientId`
 */
export type VitalsLiveDataPayload = Record<PatientId, VitalsLiveDataMeasures>;

export const VitalsQueryParamsSchema = VitalTimeRangeOptionalSchema.superRefine(
  optionalTimestampRefinement,
);
export type VitalsQueryParams = z.infer<typeof VitalsQueryParamsSchema>;

export const VitalsPaginationTimingsSchema =
  VitalTimeRangeSchema.superRefine(timestampRefinement);
export type VitalsPaginationTimings = z.infer<
  typeof VitalsPaginationTimingsSchema
>;

const createAggregateDataSchema = <TData, TErr>(
  dataSchema: z.ZodType<TData>,
  errorSchema: z.ZodType<TErr>,
) => {
  const successSchema = z.object({
    type: z.literal('success'),
    data: dataSchema,
  });

  const defaultErrorSchema = z.object({
    type: z.literal('error'),
    error: errorSchema,
  });

  const unionSchema = z.discriminatedUnion('type', [
    successSchema,
    defaultErrorSchema,
  ]);

  return { unionSchema, successSchema, defaultErrorSchema };
};

export const {
  unionSchema: VitalsAggregateDataSchema,
  successSchema: VitalsAggregateDataSuccessSchema,
  defaultErrorSchema: VitalsAggregateDataErrorSchema,
} = createAggregateDataSchema(VitalsResultMeasuresSchema, z.any());

export const {
  unionSchema: LatestAggregateDataSchema,
  successSchema: LatestAggregateDataSuccessSchema,
  defaultErrorSchema: LatestAggregateDataErrorSchema,
} = createAggregateDataSchema(LatestResultMeasuresSchema, z.any());

export const VitalsAggregateResultSchema = z.object({
  vitals: VitalsAggregateDataSchema,
  latest: LatestAggregateDataSchema,
  next: VitalsPaginationTimingsSchema.optional(),
});
export type VitalsAggregateResult = z.infer<typeof VitalsAggregateResultSchema>;

export const VitalsAggregateMeasureResultSchema = z.object({
  start: z
    .number()
    .int()
    .min(0)
    .describe('The start timestamp in epoch format'),
  end: z.number().int().min(0).describe('The end timestamp in epoch format'),
  records: SensorTimeSeriesRecordSchema.array(),
});
export type VitalsAggregateMeasureResult = z.infer<
  typeof VitalsAggregateMeasureResultSchema
>;
